import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  Chip,
  Skeleton,
  Grid,
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import { productsApi } from '../api/products';
import Seo from '../components/Seo';

const SearchResults = () => {
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('relevance');
  const [filters] = useState({
    category: '',
    priceRange: '',
    color: '',
  });

  useEffect(() => {
    if (query) {
      searchProducts(query);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const searchProducts = async (searchQuery) => {
    setLoading(true);
    try {
      const results = await productsApi.search(searchQuery);
      setProducts(results);
    } catch (error) {
      console.error('Search error:', error);
      // Use mock data as fallback
      setProducts(getMockSearchResults(searchQuery));
    } finally {
      setLoading(false);
    }
  };

  const getMockSearchResults = (searchQuery) => {
    const allProducts = [
      {
        id: '1',
        name: 'Essential Cotton Tee',
        description: 'Soft organic cotton blend for everyday comfort',
        price: 68.00,
        originalPrice: 85.00,
        imageUrl: 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=800',
        category: 'Women',
        stock: 10,
        featured: true,
        colors: ['#000000', '#ffffff', '#e8e8e8'],
      },
      {
        id: '2',
        name: 'Performance Leggings',
        description: 'High-waisted design with four-way stretch',
        price: 118.00,
        imageUrl: 'https://images.unsplash.com/photo-1506629082955-511b1aa562c8?w=800',
        category: 'Women',
        stock: 15,
        featured: true,
        colors: ['#000000', '#4a5568', '#718096'],
      },
      {
        id: '3',
        name: 'Oversized Hoodie',
        description: 'Luxe fleece with dropped shoulders',
        price: 128.00,
        originalPrice: 148.00,
        imageUrl: 'https://images.unsplash.com/photo-1556821840-3a63f95609a7?w=800',
        category: 'Unisex',
        stock: 25,
        featured: true,
        colors: ['#f7fafc', '#e2e8f0', '#000000'],
      },
      {
        id: '4',
        name: 'Seamless Sports Bra',
        description: 'Medium support with removable padding',
        price: 58.00,
        imageUrl: 'https://images.unsplash.com/photo-1538805060514-97d9cc17730c?w=800',
        category: 'Women',
        stock: 20,
        featured: true,
        colors: ['#000000', '#ffffff'],
      },
      {
        id: '5',
        name: 'Tech Joggers',
        description: 'Moisture-wicking fabric with zipper pockets',
        price: 98.00,
        imageUrl: 'https://images.unsplash.com/photo-1552902865-b72c031ac5ea?w=800',
        category: 'Men',
        stock: 12,
        featured: true,
        colors: ['#2d3748', '#000000'],
      },
      {
        id: '6',
        name: 'Yoga Mat Carrier',
        description: 'Adjustable strap fits all mat sizes',
        price: 28.00,
        originalPrice: 35.00,
        imageUrl: 'https://images.unsplash.com/photo-1601925260368-ae2f83cf8b7f?w=800',
        category: 'Accessories',
        stock: 30,
        featured: true,
      },
      {
        id: '7',
        name: 'Cropped Tank Top',
        description: 'Ribbed texture with built-in bra',
        price: 48.00,
        imageUrl: 'https://images.unsplash.com/photo-1594633312681-425c7b97ccd1?w=800',
        category: 'Women',
        stock: 18,
        featured: true,
        colors: ['#ffffff', '#fed7d7', '#000000'],
      },
      {
        id: '8',
        name: 'Training Shorts',
        description: '5-inch inseam with liner',
        price: 68.00,
        imageUrl: 'https://images.unsplash.com/photo-1591195853828-11db59a44f6b?w=800',
        category: 'Men',
        stock: 22,
        featured: true,
        colors: ['#000000', '#1a202c', '#4a5568'],
      },
    ];

    // Filter products based on search query
    return allProducts.filter(product =>
      product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.description.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  const handleSortChange = (event) => {
    const value = event.target.value;
    setSortBy(value);

    let sorted = [...products];
    switch (value) {
      case 'price-low':
        sorted.sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        sorted.sort((a, b) => b.price - a.price);
        break;
      case 'name':
        sorted.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'relevance':
      default:
        // Keep original order
        break;
    }
    setProducts(sorted);
  };

  const activeFilterCount = Object.values(filters).filter(f => f).length;

  return (
    <Container maxWidth={false} sx={{ px: { xs: 2, md: 4 }, py: 4 }}>
      <Seo title="Search" noindex />
      {/* Search Header */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 300,
            letterSpacing: '-0.01em',
            mb: 1,
          }}
        >
          Search Results
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {loading ? (
            'Searching...'
          ) : (
            <>
              {products.length} {products.length === 1 ? 'result' : 'results'} for "{query}"
            </>
          )}
        </Typography>
      </Box>

      {/* Filters and Sort */}
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        {/* Filter Chips */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            label="FILTERS"
            variant={activeFilterCount > 0 ? 'filled' : 'outlined'}
            sx={{
              borderRadius: '20px',
              fontSize: '0.75rem',
              letterSpacing: '0.05em',
            }}
          />
          {activeFilterCount > 0 && (
            <Chip
              label={`${activeFilterCount} ACTIVE`}
              size="small"
              sx={{
                borderRadius: '20px',
                fontSize: '0.75rem',
              }}
            />
          )}
        </Box>

        {/* Sort Dropdown */}
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <Select
            value={sortBy}
            onChange={handleSortChange}
            displayEmpty
            sx={{
              fontSize: '0.875rem',
              '& .MuiSelect-select': {
                py: 1,
              },
            }}
          >
            <MenuItem value="relevance">Most Relevant</MenuItem>
            <MenuItem value="price-low">Price: Low to High</MenuItem>
            <MenuItem value="price-high">Price: High to Low</MenuItem>
            <MenuItem value="name">Name: A to Z</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Search Results Grid */}
      {loading ? (
        <Grid container spacing={{ xs: 2, md: 3 }}>
          {Array.from({ length: 8 }).map((_, index) => (
            <Grid size={6} sm={4} md={3} key={index}>
              <Skeleton variant="rectangular" height={400} />
            </Grid>
          ))}
        </Grid>
      ) : products.length > 0 ? (
        <Grid container spacing={{ xs: 2, md: 3 }}>
          {products.map((product) => (
            <Grid size={6} sm={4} md={3} key={product.id}>
              <ProductCard product={product} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box sx={{ textAlign: 'center', py: 10 }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 300 }}>
            No results found
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            Try adjusting your search or browse our categories
          </Typography>
          <Button
            variant="outlined"
            onClick={() => window.location.href = '/products'}
            sx={{
              borderColor: '#000000',
              color: '#000000',
              px: 4,
              '&:hover': {
                backgroundColor: '#000000',
                color: '#ffffff',
                borderColor: '#000000',
              },
            }}
          >
            BROWSE ALL PRODUCTS
          </Button>
        </Box>
      )}
    </Container>
  );
};

export default SearchResults;